import React, { useState } from 'react';
import { GlassCard } from '../../glass';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const GREEN = '#d09d4c'; // profit = gold
const RED = '#8e9196'; // loss = grey

export const formatRupees = (value) =>
  `${value < 0 ? '-' : ''}₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(value))}`;

// "2026-10-06T00:00:00Z" -> "06 Oct"
const shortDate = (d) => {
  const [, m, day] = String(d).slice(0, 10).split('-');
  return m && day ? `${day} ${MONTHS[Number(m) - 1] || m}` : String(d).slice(0, 10);
};

// Round axis numbers (0, 450, 900 ...) so the labels look clean.
const niceScale = (minVal, maxVal, ticks = 4) => {
  const lo = Math.min(0, minVal);
  const hi = Math.max(0, maxVal);
  const raw = (hi - lo || 1) / ticks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) || raw;
  const start = Math.floor(lo / step) * step;
  const list = [];
  for (let v = start; v <= Math.ceil(hi / step) * step + step / 2; v += step) list.push(Math.round(v * 100) / 100);
  return { start: list[0], end: list[list.length - 1], list };
};

const axisLabel = (v) =>
  Math.abs(v) >= 100000
    ? new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(v)
    : new Intl.NumberFormat('en-IN').format(v);

/** One card in the "Daily Net Cumulative P&L" style: title, subtitle, gold bars, divider, footer. */
const PnlBarChart = ({ title, subtitle, series, footerMain, footerSub }) => {
  const [hover, setHover] = useState(null);

  const W = 600;
  const H = 280;
  const pad = { top: 30, right: 14, bottom: 38, left: 60 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const values = series.map((s) => s.value);
  const { start, end, list } = niceScale(Math.min(0, ...values), Math.max(0, ...values));
  const y = (v) => pad.top + ((end - v) / (end - start || 1)) * plotH;
  const zeroY = y(0);

  const slot = plotW / Math.max(series.length, 1);
  const barW = Math.min(26, slot * 0.55);
  const cx = (i) => pad.left + i * slot + slot / 2;
  const peak = values.indexOf(Math.max(...values)); // the highest bar is solid by default
  const active = hover !== null ? hover : peak;
  const labelStep = Math.ceil(series.length / 6);
  const showLabel = (i) => series.length <= 8 || i % labelStep === 0;

  return (
    <GlassCard style={{ padding: '22px 22px 18px' }}>
      <h3 className="glass-title" style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h3>
      <div className="glass-muted" style={{ marginTop: 4, fontSize: 13 }}>{subtitle}</div>

      {series.length === 0 ? (
        <p className="glass-muted" style={{ margin: '32px 0' }}>No daily P&amp;L data available.</p>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title}
            style={{ width: '100%', height: 'auto', display: 'block', marginTop: 14 }}>
            {list.map((t) => (
              <g key={t}>
                <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)}
                  stroke="rgba(255,255,255,0.10)" strokeDasharray={t === 0 ? undefined : '3 5'} />
                <text x={pad.left - 10} y={y(t) + 4} textAnchor="end" fontSize="11"
                  fill="rgba(242,239,232,0.55)">{axisLabel(t)}</text>
              </g>
            ))}

            {series.map((s, i) => {
              const top = Math.min(y(s.value), zeroY);
              const h = Math.max(2, Math.abs(zeroY - y(s.value)));
              const on = i === active;
              const positive = s.value >= 0;
              return (
                <g key={`${s.date}-${i}`} style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => setHover(i)}>
                  {/* wide invisible area so thin bars are easy to hover */}
                  <rect x={pad.left + i * slot} y={pad.top} width={slot} height={plotH} fill="transparent" />
                  <rect x={cx(i) - barW / 2} y={top} width={barW} height={h} rx={Math.min(6, h / 2)}
                    fill={positive ? '#d09d4c' : '#8e9196'} opacity={on ? 1 : 0.28} />
                  {on && (
                    <text x={cx(i)} y={positive ? top - 8 : top + h + 16} textAnchor="middle" fontSize="12"
                      fontWeight="600" fill={positive ? '#d09d4c' : '#8e9196'}>
                      {formatRupees(s.value)}
                    </text>
                  )}
                  {showLabel(i) && (
                    <text x={cx(i)} y={H - 12} textAnchor="middle" fontSize="11" fill="rgba(242,239,232,0.55)">
                      {shortDate(s.date)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          <div style={{ borderTop: '1px solid var(--g-border)', marginTop: 8, paddingTop: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{footerMain}</div>
            <div className="glass-muted" style={{ marginTop: 4, fontSize: 13 }}>{footerSub}</div>
          </div>
        </>
      )}
    </GlassCard>
  );
};

/** data = [{ date, pnl }] sorted by date */
export const DailyNetCumulativeChart = ({ data = [] }) => {
  let running = 0;
  const series = data.map((d) => {
    running += d.pnl;
    return { date: d.date, value: running };
  });

  let footerMain = `Cumulative net ${formatRupees(series.length ? series[series.length - 1].value : 0)}`;
  if (series.length >= 2 && series[series.length - 2].value !== 0) {
    const prev = series[series.length - 2].value;
    const last = series[series.length - 1].value;
    const pct = ((last - prev) / Math.abs(prev)) * 100;
    const up = pct >= 0;
    footerMain = (
      <>
        Trending {up ? 'up' : 'down'} by{' '}
        <span style={{ color: up ? GREEN : RED, fontWeight: 700 }}>{Math.abs(pct).toFixed(1)}%</span>{' '}
        vs previous day {up ? '↗' : '↘'}
      </>
    );
  }

  return (
    <PnlBarChart
      title="Daily Net Cumulative P&L"
      subtitle="Selected period performance"
      series={series}
      footerMain={footerMain}
      footerSub="Showing Daily Net Cumulative P&L"
    />
  );
};

export const NetDailyPnlChart = ({ data = [] }) => {
  const series = data.map((d) => ({ date: d.date, value: d.pnl }));
  const net = series.reduce((sum, s) => sum + s.value, 0);
  const up = net >= 0;

  return (
    <PnlBarChart
      title="Net Daily P&L"
      subtitle="Daily profit or loss for the selected period"
      series={series}
      footerMain={
        <>
          Net{' '}
          <span style={{ color: up ? GREEN : RED, fontWeight: 700 }}>{formatRupees(net)}</span> over{' '}
          {series.length} trading day{series.length === 1 ? '' : 's'} {up ? '↗' : '↘'}
        </>
      }
      footerSub="Showing Net Daily P&L"
    />
  );
};